import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { EnvelopeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { SyndicateTypeEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CoSyndicateApplicableUserSlideOver, {
  CoSyndicateApplicableUser,
} from 'routes/SyndicateDashboard/SyndicateNetwork/Invites/CoSyndicateApplicableUserSlideOver';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';
import { FormInput, FormPanel } from 'components/FormPanel';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import debounce from 'utils/debounce';
import { getDeterministicColor } from 'utils/random-colors';

import InviteUsersEmailPreviewDialogButton from './InviteUsersEmailPreviewDialogButton';

const DEAL_INFORMATION_INVITE_QUERY = gql(`
  query DealInformationForInvite($id: ID!, $cursor: ID, $filters: SyndicateUsersFilterType, $coSyndicateFilters: CoSyndicateApplicableUsersFilterType, $limit: Int) {
    deal(id: $id) {
      id
      permissions {
        canInviteUsers
      }
      syndicate {
        id
        type
        syndicateUsers(cursor: $cursor, filters: $filters, limit: $limit) {
          nodes {
            id
            user {
              id
              name
              email
            }
            createdAt
            statistics {
              investRate
            }
          }
          pageInfo {
            hasNextPage
            cursor
            totalCount
          }
        }
        coSyndicateApplicableUsers(filters: $coSyndicateFilters) {
          user {
            id
            name
            image {
              url
              name
            }
            email
            linkedInProfileUrl
            createdAt
            statistics {
              numberOfSyndicatesJoined
              totalInvestedAmount
              numberOfInvestments
              investRate
            }
          }
          syndicateUsers {
            id
            createdAt
            statistics {
              totalInvestedAmount
              numberOfInvestments
              investRate
            }
            syndicate {
              id
              name
              image
            }
          }
        }
      }
    }
  }
`);

const INVITE_USERS_TO_DEAL_MUTATION = gql(`
  mutation InviteUsersToDeal(
    $dealId: ID!
    $userIds: [ID]!
    $note: String
    $minimumInvestmentAmount: Int
    $maximumInvestmentAmount: Int
    $selectAll: Boolean
  ) {
    inviteUsersToDeal(
      id: $dealId
      userIds: $userIds
      note: $note
      minimumInvestmentAmount: $minimumInvestmentAmount
      maximumInvestmentAmount: $maximumInvestmentAmount
      selectAll: $selectAll
    )
  }
`);

const InviteNetwork: FC = () => {
  const { dealId } = useParams<{ syndicateId: string; dealId: string }>() as {
    syndicateId: string;
    dealId: string;
  };

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedCoSyndicateUserIds, setSelectedCoSyndicateUserIds] = useState<string[]>([]);
  const [coSyndicateApplicableUserOnDisplay, setCoSyndicateApplicableUserOnDisplay] =
    useState<CoSyndicateApplicableUser | null>(null);
  const [openEmailPreview, setOpenEmailPreview] = useState(false);
  const [formData, setFormData] = useState<any>({
    note: '',
    minimumInvestmentAmount: 300000,
    maximumInvestmentAmount: 1000000,
  });
  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const [selectAll, setSelectAll] = useState<boolean>(false);
  // Bumping the key remounts the table, which clears its internal row selection.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const handleOnSelect = (selectedIds: Array<string>) => {
    setSelectedCoSyndicateUserIds([]);
    setSelectAll(false);
    setSelectedUserIds(selectedIds);
  };

  const { loading, error, data, refetch, variables, fetchMore, networkStatus } = useQuery(
    DEAL_INFORMATION_INVITE_QUERY,
    {
      variables: {
        id: dealId,
        filters: {
          isEligibleForDealId: dealId,
          text: '',
        },
        coSyndicateFilters: {
          syndicateId: undefined,
          text: undefined,
        },
        limit: 30,
      },
    }
  );

  const [inviteUsersToDeal, { loading: inviteUsersToDealLoading, error: inviteUsersToDealError }] =
    useMutation(INVITE_USERS_TO_DEAL_MUTATION, {
      refetchQueries: ['GetActiveInvites', 'DealInformationForInvite'],
    });

  const coSyndicateApplicableUsers = useMemo(
    () => data?.deal?.syndicate?.coSyndicateApplicableUsers || [],
    [data]
  );

  const uniqueCoSyndicatesWithColors = useMemo(() => {
    if (!coSyndicateApplicableUsers) return [];
    const allSyndicates = coSyndicateApplicableUsers.flatMap(
      coSyndicate =>
        coSyndicate?.syndicateUsers?.map(syndicateUser => syndicateUser.syndicate) || []
    );
    const uniqueSyndicatesMap = new Map();
    allSyndicates.forEach((syndicate, index) => {
      if (syndicate && syndicate.id && !uniqueSyndicatesMap.has(syndicate.id)) {
        uniqueSyndicatesMap.set(syndicate.id, {
          ...syndicate,
          color: getDeterministicColor(index.toString()),
        });
      }
    });
    return Array.from(uniqueSyndicatesMap.values());
  }, [coSyndicateApplicableUsers]);

  if (loading) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const { nodes: syndicateUsers, pageInfo } = data.deal.syndicate.syndicateUsers;
  const isRuvSyndicate = data.deal.syndicate.type === SyndicateTypeEnumType.Ruv;
  const isCoSyndicate = data.deal.syndicate.type === SyndicateTypeEnumType.CoSyndicate;
  const canInviteUsers = data.deal.permissions.canInviteUsers;

  const selectedCount = selectAll ? pageInfo.totalCount : selectedUserIds.length;
  const hasSelection = selectedCount > 0;
  // With every user already on screen, "select all" would add nothing and only confuses the count.
  const canOfferSelectAll =
    pageInfo.hasNextPage && selectedUserIds.length === syndicateUsers.length && !selectAll;

  function clearSelection() {
    setSelectAll(false);
    setSelectedUserIds([]);
    setSelectionResetKey(key => key + 1);
  }

  const onSubmitAfterEmailPreview = async (data = formData) => {
    inviteUsersToDeal({
      variables: {
        dealId,
        userIds: [...selectedUserIds, ...selectedCoSyndicateUserIds],
        note: data?.note,
        minimumInvestmentAmount: data?.minimumInvestmentAmount,
        maximumInvestmentAmount: data?.maximumInvestmentAmount,
        selectAll,
      },
    });
  };

  const filters = [
    {
      type: 'syndicateId',
      label: 'Syndicate',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...uniqueCoSyndicatesWithColors.map(syndicate => ({
          label: syndicate.name,
          value: syndicate.id,
        })),
      ],
    },
  ];

  return (
    <>
      <CoSyndicateApplicableUserSlideOver
        open={!!coSyndicateApplicableUserOnDisplay}
        coSyndicateApplicableUser={coSyndicateApplicableUserOnDisplay}
        onClose={() => setCoSyndicateApplicableUserOnDisplay(null)}
      />
      {!canInviteUsers && (
        <Alert
          type="warning"
          title="Invites Disabled"
          message="You can only invite users to a deal that is in the raising state."
        />
      )}

      {!isRuvSyndicate && (
        <>
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="max-w-xl mb-3">
              <h3 className="text-lg font-semibold leading-6 text-gray-700">Invite your network</h3>
              <p className="mt-1 text-md text-gray-500">
                Send out invites to your network to join this deal.
              </p>
            </div>
          </div>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="w-full sm:w-72">
              <div className="relative">
                {/* TextField's leadingIcon only adds left padding, so the icon is drawn here, above the input. */}
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 z-10 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <TextField
                  fullWidth
                  leadingIcon
                  placeholder="Search for an investor"
                  onChange={debounce(e => {
                    refetch({
                      filters: {
                        ...variables?.filters,
                        isEligibleForDealId: dealId,
                        text: e.target.value,
                      },
                    });
                  }, 300)}
                />
              </div>
            </div>
            <span className="text-sm text-gray-500">
              {syndicateUsers.length} of {pageInfo.totalCount} users
            </span>
          </div>
          <div
            className={classNames(
              'mb-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4 transition-colors',
              hasSelection ? 'border-indigo-200 bg-indigo-50/50' : 'border-gray-200 bg-white'
            )}
          >
            <div className="flex items-center gap-3">
              <span
                className={classNames(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                  hasSelection ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'
                )}
              >
                <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Invite investors</h3>
                {hasSelection ? (
                  <p className="mt-0.5 text-sm text-gray-600">
                    <span className="font-semibold text-indigo-700">{selectedCount}</span> of{' '}
                    {pageInfo.totalCount} users selected
                    <button
                      type="button"
                      className="ml-3 text-indigo-600 hover:underline"
                      onClick={clearSelection}
                    >
                      Clear
                    </button>
                    {canOfferSelectAll && (
                      <button
                        type="button"
                        className="ml-3 text-indigo-600 hover:underline"
                        onClick={() => setSelectAll(true)}
                      >
                        Select all {pageInfo.totalCount}
                      </button>
                    )}
                  </p>
                ) : (
                  <p className="mt-0.5 text-sm text-gray-500">
                    Tick investors in the table, then preview the invitation email before sending.
                  </p>
                )}
              </div>
            </div>
            <Button
              disabled={selectedUserIds.length === 0 || !canInviteUsers}
              onClick={() => setOpenEmailPreview(true)}
            >
              Preview Invitation
            </Button>
          </div>
          <InviteUsersEmailPreviewDialogButton
            open={openEmailPreview}
            setOpen={setOpenEmailPreview}
            handleToastOpen={handleToastOpen}
            onSubmit={onSubmitAfterEmailPreview}
            formData={formData}
            setFormData={setFormData}
            dealId={dealId}
            loading={inviteUsersToDealLoading}
            error={inviteUsersToDealError}
          />
          <div className="mb-8">
            <DataTable
              key={selectionResetKey}
              data={syndicateUsers}
              columns={[
                {
                  label: 'Select',
                  fieldName: 'user.id',
                  type: 'SELECT',
                },
                {
                  label: 'Name',
                  fieldName: 'user.name',
                },
                {
                  label: 'Email',
                  fieldName: 'user.email',
                  type: 'COPY_STRING',
                },
                {
                  label: 'Joined Syndicate',
                  fieldName: 'createdAt',
                  type: 'DATE',
                },
                {
                  label: 'Invest Rate In Your Syndicate',
                  fieldName: 'statistics.investRate',
                  type: 'PERCENTAGE',
                },
              ]}
              onSelect={handleOnSelect}
              hasNextPage={pageInfo.hasNextPage}
              filterLoading={networkStatus === NetworkStatus.setVariables}
              onLoadMore={() =>
                fetchMore({
                  variables: {
                    cursor: pageInfo.cursor,
                  },
                })
              }
              emptyListTitle="No network users available"
              emptyListDescription="Invite eligible users to see them here."
            />
          </div>
        </>
      )}

      {isCoSyndicate && coSyndicateApplicableUsers && (
        <>
          <div className="mt-8 sm:flex sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <h3 className="text-lg font-semibold leading-6 text-gray-700">
                Find previous investors
              </h3>
              <p className="mt-1 text-md text-gray-500">
                Find previous investors who have invested in your syndicate.
              </p>
            </div>
          </div>
          <FilterPanel
            activeFilters={variables?.coSyndicateFilters}
            filters={filters}
            refetch={refetch}
            filterKey="coSyndicateFilters"
          />
          <TextField
            placeholder="Search for an investor"
            onChange={debounce(e => {
              refetch({
                coSyndicateFilters: {
                  ...variables?.coSyndicateFilters,
                  text: e.target.value,
                },
              });
            }, 500)}
          />
          <DataTable
            data={coSyndicateApplicableUsers}
            onClick={user => setCoSyndicateApplicableUserOnDisplay(user)}
            emptyListTitle="No network users available"
            emptyListDescription="Invite eligible users to see them here."
            columns={[
              {
                label: 'Select',
                fieldName: 'user.id',
                type: 'SELECT',
              },
              {
                label: 'Name',
                fieldName: 'user.name',
              },
              {
                label: 'Email',
                fieldName: 'user.email',
                type: 'COPY_STRING',
              },
              {
                label: 'Associations',
                fieldName: 'syndicateUsers',
                type: 'CUSTOM',
                customValue: (value: any) => {
                  return (
                    <div className="flex flex-col gap-2">
                      {value.map((syndicateUser: any) => (
                        <Badge
                          key={syndicateUser.syndicate.id}
                          label={syndicateUser.syndicate.name}
                          color={
                            uniqueCoSyndicatesWithColors?.find(
                              s => s?.id === syndicateUser.syndicate.id
                            )?.color
                          }
                        />
                      ))}
                    </div>
                  );
                },
              },
            ]}
            onSelect={(userIds: string[]) => {
              setSelectedUserIds([]);
              setSelectedCoSyndicateUserIds(userIds);
            }}
          />
        </>
      )}

      {selectedCoSyndicateUserIds && selectedCoSyndicateUserIds.length && canInviteUsers ? (
        <FormPanel
          loading={inviteUsersToDealLoading}
          error={inviteUsersToDealError}
          onSubmit={data => {
            setFormData(data);
            setOpenEmailPreview(true);
          }}
          submitButtonLabel="Preview Invitation"
        >
          <FormInput type="textarea" fieldName="note" label="Note" defaultValue={''} />
          <FormInput
            type="currency"
            fieldName="minimumInvestmentAmount"
            label="Minimum Investment Amount"
            defaultValue={300000}
          />
          <FormInput
            type="currency"
            fieldName="maximumInvestmentAmount"
            label="Maximum Investment Amount"
            defaultValue={1000000}
          />
        </FormPanel>
      ) : null}

      <Toast
        title="Success"
        message={'Invites sent successfully'}
        show={showToast}
        handleClose={handleToastClose}
      />
    </>
  );
};

export default InviteNetwork;
